import { Suspense, lazy, useEffect, type ComponentType, type LazyExoticComponent } from "react";

import ErrorBoundary from "../components/ErrorBoundary";
import Skeleton from "../components/states/Skeleton";
import { createChunkLoader } from "../lib/chunkRecovery.mjs";
import type { DashboardManifest, ManifestTab, ManifestWidget } from "../lib/manifest";
import { REPORT_IDS } from "./registry.mjs";

/** What every report port receives from TabRoute. */
export interface ReportProps {
  tab: ManifestTab;
  manifest: DashboardManifest;
  /** The tab's widgets after the global filters; a port reads its own data
   * through the same lib hooks the widgets use, so numbers stay one source. */
  widgets: ManifestWidget[];
  tenantId: string | null;
}

type ReportModule = { default: ComponentType<ReportProps> };
type ReportComponent = LazyExoticComponent<ComponentType<ReportProps>>;

/**
 * id -> the dynamic import of its port. Each port is its own chunk: a
 * client's report page carries the client's CSS and markup, which nothing
 * else on the dashboard needs. Add the case when the port lands, and its id
 * to registry.mjs.
 */
function importReport(id: string): Promise<ReportModule> | null {
  switch (id) {
    case "ar_aging":
      return import("./ar_aging/ArAgingReport");
    case "disposal":
      return import("./disposal/DisposalReport");
    case "containers":
      return import("./containers/ContainersReport");
    case "incidents":
      return import("./incidents/IncidentsReport");
    case "ro_efficiency":
      return import("./ro_efficiency/RoEfficiencyReport");
    case "route_profitability":
      return import("./route_profitability/RouteProfitabilityReport");
    case "price_increase":
      return import("./price_increase/PriceIncreaseReport");
    case "revenue":
      return import("./revenue/RevenueReport");
    case "dot_audit":
      return import("./dot_audit/DotAuditReport");
    case "sl_cart":
      return import("./sl_cart/SlCartReport");
    case "capital":
      return import("./capital/CapitalReport");
    default:
      return null;
  }
}

/** sessionStorage, or null where it cannot be used (private mode, blocked). */
function sessionStore(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

/**
 * AJ 2026-10-01 ("pages going white"): one shared, recoverable loader for the
 * report chunks (lib/chunkRecovery.mjs). A chunk that fails to load because a
 * deploy replaced it reloads the page once, by itself; a second failure goes
 * to the error boundary below. A hover or the idle prefetch shares the same
 * request as the click that follows.
 */
const chunks = createChunkLoader<ReportModule>(importReport, {
  storage: sessionStore(),
  reload: () => window.location.reload(),
  warn: (message) => console.warn(message),
});

const cache = new Map<string, ReportComponent | null>();

function load(id: string): ReportComponent {
  // One lazy component per report for the life of the page. If its chunk
  // failed to load and the automatic reload was already spent, the rejection
  // stays with it, so every visit shows the error card (whose button reloads)
  // rather than importing the chunk a second time in this document.
  return lazy(() => chunks.forRoute(id));
}

/** The port for a tab's `report` id, or null when no port is registered. */
export function reportComponent(id: string | undefined): ReportComponent | null {
  if (!id) return null;
  if (!cache.has(id)) cache.set(id, REPORT_IDS.includes(id) ? load(id) : null);
  return cache.get(id) ?? null;
}

/**
 * Fetch the chunks of every report a tenant's menu lists, one after another,
 * once the browser is idle. A tab that already holds its chunks cannot go
 * white on a later deploy, and no report waits on its chunk when clicked.
 * Returns a cancel function.
 */
export function prefetchReportsWhenIdle(ids: ReadonlyArray<string | undefined>): () => void {
  const todo = [...new Set(ids.filter((id): id is string => !!id && REPORT_IDS.includes(id)))];
  let cancelled = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const idle = (fn: () => void) => {
    const ric = (window as unknown as { requestIdleCallback?: (cb: () => void) => void })
      .requestIdleCallback;
    if (typeof ric === "function") ric(fn);
    else timer = setTimeout(fn, 200);
  };
  const next = () => {
    if (cancelled) return;
    const id = todo.shift();
    if (!id) return;
    // One at a time: the chunks never compete with the open tab's data reads.
    chunks
      .load(id)
      .catch(() => undefined)
      .then(() => idle(next));
  };
  // Let the first tab paint and start its reads before the first prefetch.
  timer = setTimeout(() => idle(next), 1500);
  return () => {
    cancelled = true;
    if (timer !== null) clearTimeout(timer);
  };
}

/** The shell's hook: prefetch the report chunks of the manifest's tabs. */
export function usePrefetchReports(tabs: ReadonlyArray<ManifestTab> | undefined): void {
  const ids = (tabs ?? [])
    .map((tab) => tab.report)
    .filter((id): id is string => !!id && REPORT_IDS.includes(id))
    .join(",");
  useEffect(() => {
    if (!ids) return;
    return prefetchReportsWhenIdle(ids.split(","));
  }, [ids]);
}

/** The loading state of a report whose chunk is on its way. */
function ReportLoading(props: { label: string }) {
  return (
    <div style={{ padding: "24px 32px", display: "grid", gap: 16 }}>
      <Skeleton height={56} />
      <Skeleton height={360} label={`Loading ${props.label}`} />
    </div>
  );
}

/**
 * A report port inside its own error boundary (AJ 2026-10-01: a report that
 * throws, or whose chunk cannot load, shows a card with Retry, never a white
 * page). The boundary resets when the reader opens another report.
 */
export function ReportPage(props: ReportProps & { component: ReportComponent }) {
  const { component: Report, ...rest } = props;
  const label = rest.tab.label ?? rest.tab.id ?? "report";
  return (
    <ErrorBoundary scope="report" resetKey={rest.tab.report ?? rest.tab.id ?? label} label={label}>
      <Suspense fallback={<ReportLoading label={label} />}>
        <Report {...rest} />
      </Suspense>
    </ErrorBoundary>
  );
}
